import type {
    AccountActivateRequest,
    AdministrativeUnitCodebookResponse,
    AuthenticatePinRequest,
    AuthRequest,
    AuthResponse,
    CancelVendorCustomRequest,
    ChangeApiUrlRequest,
    ChangeEmailRequest,
    ChangePasswordRequest,
    ChangeSecretWordRequest,
    CountryCodebookDTO,
    CurrencyInfo,
    DisputeRequest,
    ExistingUserVendorCustomRequest,
    IdentityCardValidationRequest,
    NewPasswordRequest,
    OffersSizeDTO,
    Page,
    PasswordResetRequest,
    PaymentPlanDTO,
    PaymentPlanRequestDTO,
    PersonBaseInfoDTO,
    PersonVerifyRequest,
    PostZipCodebookDTO,
    PreQualifyQrCodeResponse,
    ResultPageDTO,
    SecurityQuestionChangeRequest,
    SecurityQuestionDTO,
    SendPreQualifyNotificationRequest,
    SendPreQualifySmsRequest,
    SessionInfoDTO,
    SettlementCodebookDTO,
    StreetCodebookDTO,
    TransactionDeliveryRequest,
    TransactionPartialDeliveryRequest,
    TransactionPartialDeliveryResponse,
    TransactionRequestDTO,
    TransactionVendorDTO,
    VendorAgentRequestDTO,
    VendorAgentResponseDTO,
    VendorContactChangeRequest,
    VendorCustomRequest,
    VendorDataDTO,
    VendorPaymentInvoiceResponse,
    VendorPaymentInvoiceSearchRequest,
    VendorRemoteOfferRequest,
    VendorRequest,
    VendorRequestInfoDTO,
    VendorUser,
    VendorUserPasswordChangeRequestDTO,
    VendorUserRequestDTO,
    VendorUserResponseDTO,
    VendorUserRoleDTO,
    VendorValidUntilToken,
} from '@leanpay/models'
import { HttpClient } from '@leanpay/http-client'
import { storage } from '@leanpay/local-storage'
import { toAdministrativeUnit, toVendorUser } from '@leanpay/models'
import { STORAGE_KEY } from '@leanpay/utils'

import { vendorErrorHandler } from '../errorHandler'
import { clientInterceptor } from '../interceptors'

class VendorService {
    _httpClient = new HttpClient({
        config: { baseURL: window.location.origin },
        interceptorConfig: {
            onRequestFulfilled: clientInterceptor as any,
            onRejected: vendorErrorHandler,
        },
    })
    private _sessionCheckUrl = 'api/session-info'
    private _authenticateUrl = 'api/authenticate'
    private _vendorRequestInfoUrl = 'api/vendor-request/info'
    private _customOffersUrl = 'api/vendor-request/custom/all'
    private _customOfferUrl = 'api/vendor-request/custom'
    private _vendorUsersPasswordChangeUrl = 'api/vendor/user/password-change'
    private _vendorUsersUrl = 'api/vendor/user'
    private _verificationAgentsUrl = 'api/vendor/agent'
    private _createOfferUrl = 'api/vendor-request/v2/custom'
    private _createRemoteOfferUrl = 'api/vendor-request/remote'
    private _disputeTransactionUrl = 'api/transaction/dispute'
    private _cancelOfferUrl = 'api/vendor-request/custom/cancel'
    private _invoicesSearchUrl = 'api/vendor/payment-invoice'
    private _transactionsSearchUrl = 'api/transaction/search'
    private _transactionUrl = 'api/transaction'
    private _transactionDeliveryUrl = 'api/transaction/delivery'
    private _transactionPartialDeliveryUrl = 'api/transaction/partial-delivery'
    private _vendorUsersPasswordResetUrl = 'api/vendor/user/password-reset'
    private _vendorUsersRolesUrl = 'api/vendor/user/roles'
    private _calculatePaymentPlansUrl = 'api/customer/calculate-payment-plan'
    private _customerBaseInfoUrl = 'api/person/base-info'
    private _verifyPersonUrl = 'api/person/verify'
    private _requestPinUrl = 'api/person/request-pin'
    private _authenticatePinUrl = 'api/person/verify-pin'
    private _administrativeUnitsUrl = 'api/codebook/administrative-units'
    private _validateIdentityCardUrl = 'api/vendor-request/validate-identity-card'
    private _createOfferForVerifiedUserUrl = 'api/vendor-request/custom-existing-user'
    private _vendorDataUrl = 'api/vendor/data'
    private _changeContactUrl = 'api/vendor/change-contact'
    private _changeSecurityQuestionUrl = 'api/vendor/profile/change-security-question'
    private _changeEmailUrl = 'api/vendor/change-email'
    private _securityQuestionsUrl = 'api/vendor/profile/security-questions'
    private _changePasswordUrl = 'api/vendor/profile/change-password'
    private _changeApiUrlUrl = 'api/vendor/change-api-url'
    private _changeSecretWordUrl = 'api/vendor/change-secret-word'
    private _verifyEmailUrl = 'api/vendor/verify-email'
    private _vendorResendVerifyEmail = 'api/vendor/resend-verify-email'
    private _requestPasswordResetUrl = 'api/vendor/profile/request-password-reset'
    private _qrCodeUrl = 'api/pre-qualify/qr-code'
    private _sendSmsUrl = 'api/pre-qualify/send-sms'
    private _sendNotification = 'api/pre-qualify/send-notification'
    private _passwordResetUrl = 'api/vendor/profile/password-reset'
    private _currencyUrl = 'api/currency'
    private _postZipsUrl = 'api/codebook/post-zips'
    private _countryCodebookUrl = 'api/codebook/countries'
    private _streetsByPostZipUrl = '/api/codebook/streets/post-zip/'
    private _settlementsByStreetUrl = '/api/codebook/settlements/street/'
    private _settlementsByPostZipUrl = '/api/codebook/settlements/post-zip'
    private _streetsBySettlementUrl = '/api/codebook/streets'

    async sendNotification(request: SendPreQualifyNotificationRequest) {
        const { data } = await this._httpClient.post<void>(this._sendNotification, request)
        return data
    }

    async sendSms(request: SendPreQualifySmsRequest) {
        const { data } = await this._httpClient.post<void>(this._sendSmsUrl, request)
        return data
    }

    async getQrCode() {
        const { data } = await this._httpClient.get<PreQualifyQrCodeResponse>(this._qrCodeUrl)
        return data
    }

    async passwordSet(request: NewPasswordRequest) {
        const { data } = await this._httpClient.post(this._passwordResetUrl, request)

        return data
    }

    async getSessionInfo() {
        const { data } = await this._httpClient.get<SessionInfoDTO>(this._sessionCheckUrl)
        storage.save(STORAGE_KEY.SESSION_INFO, data)

        return data
    }

    async requestPasswordReset(request: PasswordResetRequest) {
        const { data } = await this._httpClient.post(this._requestPasswordResetUrl, request)

        return data
    }

    async resendVerifyEmail() {
        const { data } = await this._httpClient.post(this._vendorResendVerifyEmail, {})

        return data
    }

    async verifyEmail(request: AccountActivateRequest) {
        const { data } = await this._httpClient.post(this._verifyEmailUrl, request)

        return data
    }

    async changePassword(request: ChangePasswordRequest) {
        const { data } = await this._httpClient.post(this._changePasswordUrl, request)
        return data
    }

    async changeApiUrl(request: ChangeApiUrlRequest) {
        const { data } = await this._httpClient.post(this._changeApiUrlUrl, request)
        return data
    }

    async changeSecretWord(request: ChangeSecretWordRequest) {
        const { data } = await this._httpClient.post(this._changeSecretWordUrl, request)
        return data
    }

    async getSecurityQuestions(language = 'en') {
        const url = `${this._securityQuestionsUrl}?language=${language}`
        const { data } = await this._httpClient.get<SecurityQuestionDTO[]>(url)

        return data
    }

    async changeSecurityQuestion(request: SecurityQuestionChangeRequest) {
        const { data } = await this._httpClient.post(this._changeSecurityQuestionUrl, request)

        return data
    }

    async changeEmail(request: ChangeEmailRequest) {
        const { data } = await this._httpClient.post(this._changeEmailUrl, request)

        return data
    }

    async updateContactInfo(request: VendorContactChangeRequest) {
        const { data } = await this._httpClient.post(this._changeContactUrl, request)
        return data
    }

    async getVendorData(language = 'en') {
        const url = `${this._vendorDataUrl}?language=${language}`
        const response = await this._httpClient.get<VendorDataDTO>(url)

        const vendorData = {
            contactInfo: response.data.contactInfo,
            developmentInfo: response.data.developmentInfo,
            securityInfo: response.data.securityInfo,
        }
        return vendorData
    }

    async authenticate(data: AuthRequest) {
        const response = await this._httpClient.post<AuthResponse>(this._authenticateUrl, data)
        return response.data
    }

    async searchVerificationAgents({
        searchCriteria,
        page,
        size,
    }: {
        searchCriteria: string
        page: number
        size: number
    }) {
        const url = `${this._verificationAgentsUrl}?searchValue=${searchCriteria}&page=${page}&size=${size}`

        const res = await this._httpClient.get<Page<VendorAgentResponseDTO>>(url)

        return {
            agents: res.data.content,
            totalElements: res.data.totalElements,
            totalPages: res.data.totalPages,
        }
    }

    async addVerificationAgent(request: VendorAgentRequestDTO) {
        const { data } = await this._httpClient.post<VendorAgentResponseDTO>(
            this._verificationAgentsUrl,
            request,
        )

        return data
    }

    async getVerificationAgent(id: number) {
        const url = `${this._verificationAgentsUrl}/${id}`
        const { data } = await this._httpClient.get<VendorAgentResponseDTO>(url)
        return data
    }

    async updateVerificationAgent(
        request: VendorAgentRequestDTO,
        id: number,
    ): Promise<VendorAgentResponseDTO> {
        const url = `${this._verificationAgentsUrl}/${id}`
        const { data } = await this._httpClient.put<VendorAgentResponseDTO>(url, request)

        return data
    }

    async getVendorUser(id: number) {
        const url = `${this._vendorUsersUrl}/${id}`
        const { data } = await this._httpClient.get<VendorUserResponseDTO>(url)

        return data
    }
    async searchVendorUsers({
        searchCriteria,
        page,
        size,
    }: {
        searchCriteria: string
        page: number
        size: number
    }) {
        const url = `${this._vendorUsersUrl}?searchValue=${searchCriteria}&page=${page}&size=${size}`

        const res = await this._httpClient.get<Page<VendorUserResponseDTO>>(url)

        return {
            users: res.data.content.map((user) => toVendorUser(user)),
            totalElements: res.data.totalElements,
            totalPages: res.data.totalPages,
        }
    }

    async addVendorUser(request: VendorUserRequestDTO) {
        const { data } = await this._httpClient.post<VendorUserResponseDTO>(
            this._vendorUsersUrl,
            request,
        )
        return toVendorUser(data)
    }
    async editVendorUser(request: VendorUserRequestDTO, id: number): Promise<VendorUser> {
        const url = `${this._vendorUsersUrl}/${id}`
        const { data } = await this._httpClient.put<VendorUserResponseDTO>(url, request)

        return toVendorUser(data)
    }

    async resetPasswordForVendorUser(request: PasswordResetRequest) {
        const { data } = await this._httpClient.post(this._vendorUsersPasswordResetUrl, request)
        return data
    }

    async changePasswordForVendorUser(request: VendorUserPasswordChangeRequestDTO) {
        const { data } = await this._httpClient.patch(this._vendorUsersPasswordChangeUrl, request)
        return data
    }

    async getVendorUserRoles() {
        const { data } = await this._httpClient.get<VendorUserRoleDTO[]>(this._vendorUsersRolesUrl)

        return data
    }

    async getVendorRequestInfo(isInStore?: boolean) {
        const response = await this._httpClient.get<VendorRequestInfoDTO>(
            this._vendorRequestInfoUrl,
            {
                params: { isInStore: isInStore ?? false },
            },
        )

        return response.data
    }

    async createOffer(request: VendorCustomRequest) {
        const response = await this._httpClient.post<VendorValidUntilToken>(
            this._createOfferUrl,
            request,
        )

        return response.data
    }

    async createRemoteOffer(request: VendorRemoteOfferRequest) {
        const response = await this._httpClient.post<VendorValidUntilToken>(
            this._createRemoteOfferUrl,
            request,
        )
        return response.data
    }

    async cancelOffer(request: CancelVendorCustomRequest) {
        const response = await this._httpClient.post<void>(this._cancelOfferUrl, request)

        return response.data
    }

    async getCustomOffers({
        offersSize,
        searchCriteria,
        createdFrom,
        createdTo,
    }: {
        offersSize: OffersSizeDTO
        searchCriteria?: string
        createdFrom?: string
        createdTo?: string
    }): Promise<{
        offers: VendorRequest[]
        totalElements: number
        totalPages: number
    }> {
        const params: Record<string, any> = {
            page: offersSize.page,
            size: offersSize.size,
        }

        if (searchCriteria) params.searchCriteria = searchCriteria
        if (createdFrom) params.createdFrom = createdFrom
        if (createdTo) params.createdTo = createdTo

        const response = await this._httpClient.get<ResultPageDTO<VendorRequest>>(
            this._customOffersUrl,
            { params },
        )

        return {
            offers: response.data.result,
            totalElements: response.data.totalElements,
            totalPages: response.data.totalPages,
        }
    }

    async searchInvoices(request: {
        payload: VendorPaymentInvoiceSearchRequest
        page: number
        size: number
    }) {
        const url = `${this._invoicesSearchUrl}?page=${request.page}&size=${request.size}`
        const { data } = await this._httpClient.post<Page<VendorPaymentInvoiceResponse>>(
            url,
            request.payload,
        )

        return {
            content: data.content,
            totalElements: data.totalElements,
            totalPages: data.totalPages,
        }
    }

    async getPaymentInvoice(id: number): Promise<VendorPaymentInvoiceResponse> {
        const response = await this._httpClient.get<VendorPaymentInvoiceResponse>(
            `${this._invoicesSearchUrl}/${id}`,
        )

        return response.data
    }

    async getCustomOffer(id: number): Promise<VendorRequest> {
        const response = await this._httpClient.get<VendorRequest>(`${this._customOfferUrl}/${id}`)

        return response.data
    }

    async disputeTransaction(request: DisputeRequest) {
        const { data } = await this._httpClient.post(this._disputeTransactionUrl, request)
        return data
    }

    async doTransactionDelivery(request: TransactionDeliveryRequest) {
        const { data } = await this._httpClient.post(this._transactionDeliveryUrl, request)
        return data
    }

    async doTransactionPartialDelivery(request: TransactionPartialDeliveryRequest) {
        const { data } = await this._httpClient.post<TransactionPartialDeliveryResponse>(
            this._transactionPartialDeliveryUrl,
            request,
        )
        return data
    }

    async getTransaction(id: number): Promise<TransactionVendorDTO> {
        const response = await this._httpClient.get<TransactionVendorDTO>(
            `${this._transactionUrl}/${id}`,
        )

        return response.data
    }

    async getTransactions({
        request,
        page,
        size,
    }: {
        request: TransactionRequestDTO
        page: number
        size: number
    }): Promise<{
        transactions: TransactionVendorDTO[]
        totalElements: number
        totalPages: number
    }> {
        const postUrl = `${this._transactionsSearchUrl}?page=${page}&size=${size}`
        const { data } = await this._httpClient.post<ResultPageDTO<TransactionVendorDTO>>(
            postUrl,
            request,
        )

        return {
            transactions: data.result,
            totalElements: data.totalElements,
            totalPages: data.totalPages,
        }
    }

    async calculatePaymentPlans(request: PaymentPlanRequestDTO) {
        const { data } = await this._httpClient.post<PaymentPlanDTO[]>(
            this._calculatePaymentPlansUrl,
            request,
        )
        return data
    }

    async getCustomerBaseInfo(phoneNumber: string) {
        const url = `${this._customerBaseInfoUrl}?phoneNumber=${phoneNumber}`
        const { data } = await this._httpClient.get<PersonBaseInfoDTO>(url)

        return data
    }

    async verifyPerson(request: PersonVerifyRequest) {
        await this._httpClient.post<void>(this._verifyPersonUrl, request)
    }

    // RO in-store OTP consent (TEC-19045). Re-calling request-pin invalidates any pending
    // OTP for the phone and issues a new one — also used for the salesperson-triggered resend.
    async requestPin(phoneNumber: string) {
        await this._httpClient.post<void>(this._requestPinUrl, { phoneNumber })
    }

    async authenticatePin(request: AuthenticatePinRequest) {
        await this._httpClient.post<void>(this._authenticatePinUrl, request)
    }

    async getAdministrativeUnits(countryCodebookId: string) {
        const url = `${this._administrativeUnitsUrl}?countryCodebookId=${countryCodebookId}`
        const { data } = await this._httpClient.get<AdministrativeUnitCodebookResponse[]>(url)
        const res = data.map((unit) => toAdministrativeUnit(unit))

        return res
    }

    async validateIdentityCard(request: IdentityCardValidationRequest) {
        const { data } = await this._httpClient.post<void>(this._validateIdentityCardUrl, request)

        return data
    }

    async createOfferForVerifiedUser(request: ExistingUserVendorCustomRequest) {
        const { data } = await this._httpClient.post<VendorValidUntilToken>(
            this._createOfferForVerifiedUserUrl,
            request,
        )

        return data
    }

    async getCurrencyFromServer() {
        const { data } = await this._httpClient.get<CurrencyInfo>(this._currencyUrl)

        return data
    }

    async getCountryCodebook() {
        const { data } = await this._httpClient.get<CountryCodebookDTO[]>(this._countryCodebookUrl)

        return data
    }

    async getPostZips(countryCodebookId: string) {
        const url = `${this._postZipsUrl}?countryCodebookId=${countryCodebookId}`
        const { data } = await this._httpClient.get<PostZipCodebookDTO[]>(url)

        return data
    }

    async getStreetsByPostZip(postZipCodebookId: string) {
        const url = `${this._streetsByPostZipUrl + postZipCodebookId}?size=10000`
        const { data } = await this._httpClient.get<Page<StreetCodebookDTO>>(url)

        return data.content
    }

    async getSettlementsByStreet(streetId: string) {
        const url = this._settlementsByStreetUrl + streetId
        const { data } = await this._httpClient.get<Page<StreetCodebookDTO>>(url)

        return data.content
    }

    async getSettlementsByPostZip(postZipCodebookId: string) {
        const url = `${this._settlementsByPostZipUrl}/${postZipCodebookId}`
        const { data } = await this._httpClient.get<SettlementCodebookDTO[]>(url)

        return data
    }

    async getStreetsBySettlement(settlementCodebookId: string) {
        const url = `${this._streetsBySettlementUrl}?settlementCodebookId=${settlementCodebookId}`
        const { data } = await this._httpClient.get<StreetCodebookDTO[]>(url)

        return data
    }
}

export const vendorService = new VendorService()
